import { FitbitCollectionStatus, FitbitWear } from '../../../services/fitbitCollectionDataService';

const utcMinute = (value: string | null) => (value ? `${value.slice(0, 10)} ${value.slice(11, 16)} UTC` : null);

// Plain-language reason for a collection that is no longer running, or null while it runs.
export function stoppedCollectionLabel(collection?: FitbitCollectionStatus | null): { reason: string; action: string } | null {
  if (!collection) return null;
  const since = collection.since ? ` since ${collection.since.slice(0, 10)}` : '';
  const status = collection.httpStatus ? ` (Fitbit answered HTTP ${collection.httpStatus})` : '';
  if (collection.state === 'connection_action_required') {
    return { reason: `Fitbit access stopped working${since}${status}`, action: 'Ask the participant to reconnect Fitbit.' };
  }
  if (collection.state !== 'held_for_review') return null;
  if (collection.autoRetry) {
    return { reason: `Fitbit kept failing temporarily${since}${status}`,
      action: `StudySync retries automatically${collection.autoRetryAfter ? ` after ${utcMinute(collection.autoRetryAfter)}` : ''}.` };
  }
  return { reason: `Collection stopped${since}${status}`, action: 'Needs review by the StudySync team.' };
}

// Watches keep about seven days of detailed data. Flagging at three days leaves the
// study team time to reach a participant before any of it is overwritten.
export const SYNC_ALERT_DAYS = 3;
const DAY_MS = 86400000;

export type SyncStatus = { lastDate: string; daysAgo: number; source: 'heart rate' | 'steps';
  heartRateDays14: number; stale: boolean };

// Heart rate needs the watch; steps can also come from the phone, so steps count only
// for participants who have never had heart rate. Dates are UTC days, as stored.
export function syncStatus(wear?: FitbitWear | null, now = new Date()): SyncStatus | null {
  const lastDate = wear?.lastHeartRateDate || wear?.lastStepsDate;
  if (!wear || !lastDate) return null;
  const today = Date.parse(now.toISOString().slice(0, 10));
  const daysAgo = Math.max(0, Math.round((today - Date.parse(lastDate)) / DAY_MS));
  const heartRateDays14 = new Set(wear.recentHeartRateDays.filter(day => {
    const time = Date.parse(day);
    return time > today - 14 * DAY_MS && time <= today;
  })).size;
  return { lastDate, daysAgo, source: wear.lastHeartRateDate ? 'heart rate' : 'steps', heartRateDays14, stale: daysAgo >= SYNC_ALERT_DAYS };
}

export function syncLabel(status: SyncStatus | null) {
  if (!status) return null;
  if (status.stale) return `Watch not synced for ${status.daysAgo} days (last ${status.source} ${status.lastDate})`;
  return `Last ${status.source} ${status.daysAgo === 0 ? 'today' : status.daysAgo === 1 ? 'yesterday' : `${status.daysAgo} days ago`}`;
}
